<template>
    <!-- this nav is a stacking context, so the avatar dropdown's own z-index counts
         only inside it - the whole header has to clear the page's sticky headers
         (wiki 7, section menus 5) or the dropdown opens behind them. Stays below the
         popup/modal layer, which starts at 1021. -->
    <nav aria-label="Header navigation panel" style="z-index: 100; position: relative;">
        <div class="container" style="position: relative; z-index: 201;">
            <site-header-search ref="search"></site-header-search>
        </div>
        <div id="site-header-component" class="site-header-component hidden-print" v-if="show">
            <a :href="homeLink"
               @click="onHomeLinkClick"
               class="site-header-component__site_logo_link"
               :aria-label="ariaLabelActivity" role="link"
               @blur="helloSaid"
            >
                <img class="site-header-component__site_logo_image" src="/static/images/mm_icon.svg" alt="MultiMe logo"
                     aria-label="">
                <site-header-notifications-counter :value="counters.activity"
                                                   v-if="counters.activity>0"></site-header-notifications-counter>
            </a>

            <a href="javascript:;" class="site-header-component__nav-item"
               role="link"
               @click.stop.prevent="onSearch"
               aria-label="Search."
               v-if="authenticated">
                <div class="">
                    <span class="site-header-component__nav-icon search"></span>
                </div>
                <div class="site-header-component__nav-item__text">
                    <span>Search</span>
                </div>
            </a>

            <a :href="stuffLink" @click="onNavClick($event, stuffLink)" class="site-header-component__nav-item"
               aria-label="My Stuff."
               v-if="authenticated">
                <div class="">
                    <span class="site-header-component__nav-icon stuff"
                          aria-label="My Stuff"></span>
                </div>
                <div class="site-header-component__nav-item__text">
                    <span>My Stuff</span>
                </div>
            </a>

            <a class="site-header-component__nav-item"
               :href="chatsLink"
               @click="onNavClick($event, chatsLink)"
               :aria-label="ariaLabelChats"
               v-if="authenticated">
                <div class="">
                    <span class="site-header-component__nav-icon conversations" aria-label="Chats"></span>
                </div>
                <div class="site-header-component__nav-item__text">
                    <span>&nbsp;&nbsp;&nbsp;&nbsp;Chats</span>
                </div>
                <site-header-notifications-counter :value="counters.chats"
                                                   v-if="counters.chats>0"></site-header-notifications-counter>
            </a>

            <a class="site-header-component__nav-item"
               :href="insightsLink" @click="onNavClick($event, insightsLink)"
               aria-label="Insights."
               v-if="showInsightsLink">
                <div class="">
                    <span class="site-header-component__nav-icon insights" aria-label="Insights">
                    </span>
                </div>
                <div class="site-header-component__nav-item__text">
                    <span>Insights</span>
                </div>
            </a>

            <a class="site-header-component__nav-item"
               :href="requestsLink" @click="onNavClick($event, requestsLink)"
               :aria-label="ariaLabelRequests"
               v-if="authenticated">
                <div class="">
                    <a class="site-header-component__nav-icon requests" aria-label="Alerts"></a>
                </div>
                <div class="site-header-component__nav-item__text">
                    <a>Alerts</a>
                </div>
                <site-header-notifications-counter
                        :value="counters.tasks"
                        v-if="counters.tasks>0"></site-header-notifications-counter>
            </a>

            <span
                    tabindex="0"
                    @keydown.enter="dropDownOpened=true"
                    role="menu"
                    aria-label="My Account Dropdown menu."
                    id="avatar-dropdown"
                    v-if="authenticated"
                    style="z-index: 1;"
                    class="site-header-component__user_avatar_link pointer"
            >
                <dropdown menu-right v-model="dropDownOpened" style="z-index: 200">
                    <!-- data-role="trigger" because uiv falls back to the dropdown's
                         firstChild, and vue 3 renders the default slot as a fragment whose
                         first child is an empty text anchor, not the avatar -->
                    <img aria-label="" :src="avatarUrl" id="avatar_header_image"
                         data-role="trigger"
                         alt="Your avatar"
                         class="site-header-component__user_avatar"/>
                    <template v-slot:dropdown>
                        <li class="site-header-component__avatar-menu__link" aria-label="About Me" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="profileStoryLink" @click="onNavClick($event, profileStoryLink)"
                               @keydown.shift.tab="dropDownOpened=false">
                                <i class="fal fa-user"></i> About Me
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="profileGroupsLink" @click="onNavClick($event, profileGroupsLink)">
                                <i class="fal fa-users"></i> My Groups
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="profileCircleLink" @click="onNavClick($event, profileCircleLink)">
                                <i class="fal fa-draw-circle"></i> My Circle
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" v-if="showMyWikiLink" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="myWikiLink" @click="onNavClick($event, myWikiLink)">
                                <i class="fal fa-user-circle"></i> My Wiki
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" v-if="showMyCalendarLink" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="myCalendarLink" @click="onNavClick($event, myCalendarLink)">
                                <i class="fal fa-calendar-alt"></i> My Calendar
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" v-if="showMyDiaryLink" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="myDiaryLink" @click="onNavClick($event, myDiaryLink)">
                                <i class="fal fa-edit"></i> My Diary
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" v-if="showMyGoalsLink" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="myGoalsLink" @click="onNavClick($event, myGoalsLink)">
                                <i class="fal fa-bullseye-pointer"></i> My {{ $customTerm("Goals") }}
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" v-if="showMyMediaLink" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="myMediaLink" @click="onNavClick($event, myMediaLink)">
                                <i class="fal fa-photo-video"></i> My Media
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" role="menuitem"
                            aria-owns="avatar-dropdown">
                            <a :href="profileSettingsLink" @click="onNavClick($event, profileSettingsLink)">
                                <i class="fal fa-cog"></i> Settings
                            </a>
                        </li>
                        <li class="site-header-component__avatar-menu__link" role="menuitem" aria-owns="avatar-dropdown"
                        >
                            <a href="/accounts/logout/" @click="onLogout" @auxclick.prevent
                               @keydown.tab="dropDownOpened=false">
                                <i class="fal fa-sign-out"></i> Log Out
                            </a>
                        </li>
                    </template>
                </dropdown>
        </span>
            <div class="site-header-component__nav-item" v-if="!authenticated&&logInVisible">
                <div class="">
                    <a :href="loginLink"
                       class="site-header-component__nav-icon login"
                       alt="Log in"></a>
                </div>
                <div class="site-header-component__nav-item__text">
                    <a :href="loginLink">Log in</a>
                </div>
            </div>

        </div>
    </nav>
</template>

<script>
    import notifications from '~/vue/plugins/notifications';
    import {DjangoUrls} from 'DjangoUrls';
    import _ from 'lodash';
    import SiteHeaderSearch from './SiteHeaderSearch.vue';
    import SiteHeaderNotificationsCounter from './SiteHeaderNotificationsCounter.vue';
    import {CurrentUserClass} from "../shared/CurrentUser";
    import {DASHBOARD_ITEMS} from "../dashboard/dashboard";
    import UserProfileResourceFactory from "../userprofile/userprofile_resource";
    import globalEventBus, {GLOBAL_EVENTS} from "shared/globalEventBus";
    import {isSpaMounted} from "~/spa/mounted";
    import RouterCheckAvailabilityMixin from "~/vue/common_components/RouterCheckAvailabilityMixin";
    import sessionHandler from "~/angular/app/session_expire_handler/SessionHandler";


    const CurrentUser = new CurrentUserClass();
    const SHOW_INSIGHT_LINK = CurrentUser.can_see_insights_link();
    const UserProfileResource = UserProfileResourceFactory();

    // The header used to be re-created by every navigation, which is what kept its counters
    // current; it outlives them now, so a navigation or a return to the tab refreshes them
    // instead. Throttled with no trailing call: the activity counter is an expensive query,
    // and a skipped refresh is picked up by the next one either way.
    const COUNTERS_REFRESH_INTERVAL = 60 * 1000;


    export default {
        name: "SiteHeader",
        mixins: [RouterCheckAvailabilityMixin],
        components: {
            SiteHeaderSearch,
            SiteHeaderNotificationsCounter,
        },
        data() {
            return {
                dropDownOpened: false,
                show: true,
                counters: {
                    activity: 0,
                    chats: 0,
                    tasks: 0,

                },
                authenticated: false,
                saidHello: false,
            };
        },
        computed: {
            ariaLabelChats() {
                if (this.counters.chats > 0) {
                    return `Chats: ${this.counters.chats} new messages.`;
                } else {
                    return 'Chats.';
                }
            },
            ariaLabelActivity() {
                let basicMessage = '';
                if (!this.saidHello) {
                    basicMessage = `Hello, ${CurrentUser.first_name_or_username}.`;
                }
                if (this.counters.activity > 0) {
                    return `${basicMessage} Recent activity: ${this.counters.activity} new notifications.`;
                } else {
                    return `${basicMessage} Recent activity.`;
                }
            },
            ariaLabelRequests() {
                if (this.counters.tasks > 0) {
                    return `Alerts: ${this.counters.tasks} new alerts.`;
                } else {
                    return 'Alerts.';
                }
            },

            showInsightsLink() {
                return this.authenticated && SHOW_INSIGHT_LINK;
            },
            avatarUrl() {
                return CurrentUser.avatar;
            },
            dashboardConfig() {
                return CurrentUser.dashboard_config;
            },
            showMyCalendarLink() {
                return !this.dashboardConfig[DASHBOARD_ITEMS.CALENDAR];
            },
            showMyGoalsLink() {
                return !this.dashboardConfig[DASHBOARD_ITEMS.GOALS];
            },
            showMyDiaryLink() {
                return !this.dashboardConfig[DASHBOARD_ITEMS.DIARY];
            },
            showMyMediaLink() {
                return !this.dashboardConfig[DASHBOARD_ITEMS.MEDIA];
            },
            showMyWikiLink() {
                // no wiki_id means no wiki of their own - nothing for "My Wiki" to point at, and
                // myWikiLink would throw on the null (see DjangoUrls' kwargs branch)
                return !!CurrentUser.wiki_id && !this.dashboardConfig[DASHBOARD_ITEMS.WIKI];
            },
            myDiaryLink() {
                return DjangoUrls['blog_detail'](CurrentUser.blog_id);
            },
            myWikiLink() {
                return DjangoUrls['users_wiki'](CurrentUser.wiki_id);
            },
            myGoalsLink() {
                return DjangoUrls['projects']();
            },
            myMediaLink() {
                return DjangoUrls['users_media'](CurrentUser.username);
            },
            myCalendarLink() {
                return DjangoUrls['users_calendar']('my');
            },
            requestsLink() {
                return DjangoUrls['tasks']();
            },
            insightsLink() {
                return DjangoUrls['stats_page']();
            },
            chatsLink() {
                return DjangoUrls['my_chats']();
            },
            stuffLink() {
                return DjangoUrls['user_dashboard']();
            },
            homeLink() {
                return DjangoUrls['user_detail']('me') + 'feed';
            },
            loginLink() {
                return DjangoUrls['auth_login']();
            },
            profileStoryLink() {
                return DjangoUrls['user_detail']('me') + 'profile';
            },
            profileGroupsLink() {
                return DjangoUrls['user_detail']('me') + 'groups-and-communities';
            },
            profileCircleLink() {
                return DjangoUrls['user_detail']('me') + 'circle';
            },
            profileSettingsLink() {
                return DjangoUrls['user_detail']('me') + 'settings';
            },
            logInVisible() {
                const uri = document.location.href;
                const NO_LOGIN_MARKERS = ['/registration/login/', '/accounts/login/', '/accounts/logout/',
                    '/accounts/logged-out/', '/invitation/code/'];
                for (let marker of NO_LOGIN_MARKERS) {
                    if (uri.indexOf(marker) !== -1) {
                        return false;
                    }
                }
                return true;
            },
        },
        methods: {
            toggleHeaderVisibility(show) {
                this.show = show;
            },
            onSearch() {
                this.$refs['search'].show();
            },
            helloSaid() {
                this.$nextTick(() => {
                    this.saidHello = true;
                });
            },
            updateCounters() {
                const run_on_feed_page = window.location.href.indexOf('/users/me/feed') !== -1;
                UserProfileResource.get_my_header_counters(CurrentUser.id, run_on_feed_page)
                    .then(resp => {
                        _.merge(this.counters, resp.data);
                    }, err => {
                        notifications.error('Can\'t load counters');
                    });
            },
            updateConversationCounter() {
                UserProfileResource.get_unread_conversations_count(CurrentUser.id)
                    .then(resp => {
                        this.counters.chats = resp.data;
                    }, err => {
                        notifications.error('Can\'t load counter');
                    });
            },
            updateActivityCounter() {
                UserProfileResource.get_unread_activity_count(CurrentUser.id)
                    .then(resp => {
                        this.counters.activity = resp.data;
                    }, err => {
                        notifications.error('Can\'t load counter');
                    });
            },
            updateTasksCounter() {
                UserProfileResource.get_unread_tasks_count(CurrentUser.id)
                    .then(resp => {
                        this.counters.tasks = resp.data;
                    }, err => {
                        notifications.error('Can\'t load counter');
                    });
            },

            // listeners of the global event bus
            onConversationsCounterChanged(val) {
                this.counters.chats = val;
            },
            onConversationsCounterIncreased() {
                this.counters.chats++;
            },
            onActivityCounterIncreased() {
                this.counters.activity++;
            },
            onTasksCounterChanged(val) {
                this.counters.tasks = val;
            },
            onTabVisibilityChanged(isVisible) {
                if (isVisible) {
                    this.refreshCounters();
                }
            },
            countersListeners() {
                return {
                    [GLOBAL_EVENTS.TAB_VISIBILITY_CHANGED]: this.onTabVisibilityChanged,
                    [GLOBAL_EVENTS.UPDATE_CONVERSATIONS_COUNTERS]: this.updateConversationCounter,
                    [GLOBAL_EVENTS.UPDATE_ACTIVITY_COUNTERS]: this.updateActivityCounter,
                    [GLOBAL_EVENTS.UPDATE_TASKS_COUNTERS]: this.updateTasksCounter,
                    [GLOBAL_EVENTS.CONVERSATIONS_UNREAD_COUNT_CHANGED]: this.onConversationsCounterChanged,
                    [GLOBAL_EVENTS.TASK_COUNT_CHANGED]: this.onTasksCounterChanged,
                    [GLOBAL_EVENTS.CONVERSATIONS_UNREAD_COUNT_INCREASED]: this.onConversationsCounterIncreased,
                    [GLOBAL_EVENTS.ACTIVITY_UNREAD_COUNT_INCREASED]: this.onActivityCounterIncreased,
                };
            },
            // end listeners
            /**
             * Follow a header link inside the router when this document can render it.
             *
             * Every one of these used to be a full page load. Five routed only while the page
             * that owned the target's router happened to be open - the header read it out of a
             * per page registry - and the rest, the whole My Stuff menu included, carried no
             * handler at all, because their targets are all in other sections and leaving one
             * section for another meant a round trip. One router covers every section now, and
             * the header's own app has it.
             *
             * Falls through to the <a href> when the app is not mounted (django's own pages -
             * 404, the unsubscribe pages, logged out - carry the header too), when the target
             * is not an app route, and when a modifier is held, which is the browser's
             * "open in a new tab".
             */
            onNavClick(e, link) {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button) {
                    return;
                }
                if (!isSpaMounted() || !this.getRouterAvailability(link)) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                this.dropDownOpened = false;
                this.$router.push(link);
            },
            onLogout(e) {
                // every click, modified or not: opening the link in a new tab would be a GET
                e.preventDefault();
                e.stopPropagation();
                this.dropDownOpened = false;
                sessionHandler.logout(e.currentTarget.href);
            },
            onHomeLinkClick(e) {
                // the home link doubles as "refresh my feed", and pushing the route you are
                // already on renders nothing - so reload when it is the one you are on
                const homeHref = this.$router.resolve(this.homeLink).href;
                if (homeHref === document.location.pathname
                    && !e.ctrlKey && !e.metaKey && isSpaMounted()) {
                    this.$loadingSpinner.show();
                    setTimeout(() => {
                        document.location.reload();
                    }, 10);
                }
                this.onNavClick(e, this.homeLink);
            },
            enableAriaHiddenElements() {
                window.document.querySelector('#site-footer')?.setAttribute('aria-hidden', 'false');
            }
        },
        created() {
            const listeners = {
                [GLOBAL_EVENTS.DISABLE_FOOTER_LOGIN_MODE]: () => this.toggleHeaderVisibility(true),
                [GLOBAL_EVENTS.ENABLE_FOOTER_LOGIN_MODE]: () => this.toggleHeaderVisibility(false),
            };
            if (CurrentUser.is_authenticated()) {
                this.authenticated = true;
                this.refreshCounters = _.throttle(this.updateCounters, COUNTERS_REFRESH_INTERVAL,
                    {trailing: false});
                // the load's own fetch, and the one that takes the leading edge, so that the
                // router's first afterEach does not fetch the same counters again
                this.refreshCounters();
                Object.assign(listeners, this.countersListeners());
                this.unsubscribeFromRouteChanges = this.$router?.afterEach(() => this.refreshCounters());
            }
            this.unsubscribeFromGlobalEvents = globalEventBus.subscribe(listeners);
        },
        mounted() {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.enableAriaHiddenElements();
                }, 200);
            });
        },
        beforeUnmount() {
            this.unsubscribeFromGlobalEvents();
            this.unsubscribeFromRouteChanges?.();
            this.refreshCounters?.cancel();
        }
    };
</script>

<style scoped lang="scss">
  @use "../styles/const" as *;

  $small_mobile_icon_size: 35px;
  $mobile_icon_size: 40px;
  $icon_size: 55px;

  .site-header-component {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-flow: row;
    flex-wrap: wrap;

    position: relative;

    &__avatar-menu {
      &__link {
        font-size: 21px;

        & > a > i {
          width: 28px;
          text-align: center;
        }

        & > a:hover {
          background-color: $lightgray;
        }
      }
    }

    a {
      text-decoration: none !important;
      color: #333;
    }

    &__site_logo_link, &__user_avatar_link {
      width: $small_mobile_icon_size;
      height: $small_mobile_icon_size;
      position: relative;
      @media all and (max-width: 350px) {
        width: $small_mobile_icon_size;
        height: $small_mobile_icon_size;
      }
      @media all and (min-width: 351px) and (max-width: 500px) {
        width: $mobile_icon_size;
        height: $mobile_icon_size;
      }
      @media all and (min-width: 501px) {
        width: $icon_size + 10px;
        height: $icon_size + 10px;
      }
      vertical-align: middle;
      display: inline-block;
    }

    &__site_logo_image {
      width: $small_mobile_icon_size;
      height: $small_mobile_icon_size;
      @media all and (max-width: 350px) {
        width: $small_mobile_icon_size;
        height: $small_mobile_icon_size;
      }
      @media all and (min-width: 351px) and (max-width: 500px) {
        width: $mobile_icon_size;
        height: $mobile_icon_size;
      }
      @media all and (min-width: 501px) {
        width: $icon_size + 10px;
        height: $icon_size + 10px;
      }
      vertical-align: middle;
    }

    &__nav-icon {
      position: relative;
      width: $small_mobile_icon_size;
      height: $small_mobile_icon_size;
      @media all and (max-width: 350px) {
        width: $small_mobile_icon_size;
        height: $small_mobile_icon_size;
      }
      @media all and (min-width: 351px) and (max-width: 500px) {
        width: $mobile_icon_size;
        height: $mobile_icon_size;
      }
      @media all and (min-width: 501px) {
        width: $icon_size;
        height: $icon_size;
      }

      background-position: center;
      background-repeat: no-repeat;
      vertical-align: middle;
      display: block;
      margin: auto;

      &.stuff {
        background-image: url(./IMG_0647.png);
        background-size: 89%;
        background-position: center;
      }

      &.search {
        background-image: url(./IMG_0646.png);
        background-size: 96%;
      }

      &.conversations {
        background-image: url(./151515.png);
        background-size: 91%;

      }

      &.requests {
        background-image: url(./np_bell_70899_484848.png);
        background-size: 102%;
      }

      &.insights {
        text-align: center;
        color: #616061;
        font-size: 30px;
        background-image: url(./insights-custom-2.png);
        background-size: 92%;

        @media (max-width: 350px) {
          font-size: 26px;
        }
        @media (min-width: 351px) and (max-width: 500px) {
          font-size: 30px;
        }
        @media (min-width: 501px) {
          font-size: 41px;
        }

        &__icon {
          display: block;
          vertical-align: top;
        }
      }

      &.login {
        background-image: url(./np_login_1187014_23BD4F_2.png);
        background-size: 95%;
      }

    }

    &__user_avatar {
      display: inline-block;
      border: 3px solid $gray;
      width: $small_mobile_icon_size;
      height: $small_mobile_icon_size;

      @media all and (max-width: 350px) {
        width: $small_mobile_icon_size;
        height: $small_mobile_icon_size;
      }
      @media all and (min-width: 351px) and (max-width: 500px) {
        width: $mobile_icon_size;
        height: $mobile_icon_size;
      }
      @media all and (min-width: 501px) {
        width: $icon_size + 10px;
        height: $icon_size + 10px;
      }
      border-radius: 50%;
    }

    &__site_logo_link {
      &:focus, &:active {
        transform: scale(1.05, 1.05);
      }
    }

    &__user_avatar_link {
      &:focus, &:active {
        transform: scale(1.05, 1.05);
      }
    }

    &__nav-item {
      display: inline-block;
      position: relative;
      vertical-align: middle;
      text-align: center;

      &:focus, &:active {
        transform: scale(1.05, 1.05);
      }

      &__text {
        margin: auto;
        text-align: center;
        font-weight: bolder;
        color: #333;
        display: none;
        @media all and (max-width: 350px) {
          display: none;
        }
        @media all and (min-width: 351px) and (max-width: 500px) {
          display: none;
        }
        @media all and (min-width: 501px) {
          font-size: 14px;
          display: inline-block;
        }
        //display: none;
      }
    }

  }

</style>
